import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { MessageSquarePlus, ArrowLeft, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import FeedbackForm from './FeedbackForm';
import SubmittedFeedbackList from './SubmittedFeedbackList';
import IntakeChat from './IntakeChat';

export default function FeedbackButton() {
  const queryClient = useQueryClient();
  const location = useLocation();

  /* ── AI Mode state (Dialog) ──
     SESSION-RESUME ARCHITECTURE:
     Session state lives on the SERVER (IntakeSession entity). The parent
     holds just the session_id so we can pass it back into IntakeChat on
     reopen. On close, we do NOT clear the session_id — that's what enables
     resume. IntakeChat uses `resumeSessionId` on mount to decide whether
     to start fresh or rehydrate from the server. */
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSessionId, setAiSessionId] = useState(null);

  /* ── Standard form state (Popover) ── */
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('new'); // 'new' | 'list' | 'edit'
  const [feedbackType, setFeedbackType] = useState('bug');
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingFeedbackId, setEditingFeedbackId] = useState(null);
  const [autoSelectFeedbackId, setAutoSelectFeedbackId] = useState(null);
  const [severity, setSeverity] = useState('medium');
  const [sourceApp, setSourceApp] = useState('');

  const ORG_TYPES = ['execution_headwind', 'capability_gap', 'process_friction', 'red_line_violation'];
  const isOrgType = ORG_TYPES.includes(feedbackType);

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  /* ── ProjectRegistry for project picker dropdown ── */
  const { data: projectRegistry } = useQuery({
    queryKey: ['projectRegistry'],
    queryFn: () => base44.entities.ProjectRegistry.list(),
    staleTime: 5 * 60 * 1000
  });

  const projectOptions = (Array.isArray(projectRegistry) ? projectRegistry : [])
    .filter(p => p.label)
    .map(p => ({ value: p.domain, label: p.label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  /* ── Auto-detect source app ── */
  const detectedSourceApp = (() => {
    var host = window.location.hostname || '';
    if (host.includes('compass') || host.includes('thecompass')) return 'COMPASS';
    if (host.includes('schedulix') || host.includes('scheduler')) return 'Schedulix';
    if (host.includes('ops-dashboard') || host.includes('opsdashboard')) return 'Ops Dashboard';
    var match = (projectOptions || []).find(p =>
      host.includes(p.value.toLowerCase()) || host.includes(p.label.toLowerCase())
    );
    return match ? match.label : 'COMPASS';
  })();

  /* Auto-detect source app from hostname on first open */
  useEffect(() => {
    if (open && mode === 'new' && !editingFeedbackId && !sourceApp) {
      setSourceApp(detectedSourceApp);
    }
  }, [open, mode, editingFeedbackId, projectOptions]);

  const resetForm = () => {
    setTitle('');
    setLink('');
    setDescription('');
    setStepsToReproduce('');
    setExpectedBehavior('');
    setActualBehavior('');
    setAttachments([]);
    setEditingFeedbackId(null);
    setSeverity('medium');
    setSourceApp('');
  };

  const loadFeedbackIntoForm = (item) => {
    setEditingFeedbackId(item.id);
    setFeedbackType(item.type || 'bug');
    setTitle(item.title || '');
    setLink(item.link || '');
    setDescription(item.description || '');
    setStepsToReproduce(item.steps_to_reproduce || '');
    setExpectedBehavior(item.expected_behavior || '');
    setActualBehavior(item.actual_behavior || '');
    setAttachments(item.attachments || []);
    /* FB-69fcc224 Stage 1: derive source dropdown from project_id (canonical FK).
       project_id is required after backfill; if absent on a legacy unbackfilled card,
       leave dropdown empty and the user must pick before re-submitting. */
    const matchedProject = (Array.isArray(projectRegistry) ? projectRegistry : [])
      .find(p => p.id === item.project_id);
    setSourceApp(matchedProject?.label || '');
  };

  // Deep link: auto-open submitted list and select a specific feedback item
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const feedbackId = params.get('feedbackId');
    if (feedbackId) {
      const url = new URL(window.location.href);
      url.searchParams.delete('feedbackId');
      window.history.replaceState({}, '', url.toString());
      setMode('list');
      setOpen(true);
      setAutoSelectFeedbackId(feedbackId);
    }
  }, [location.search]);

  // Auto-populate link with current page when popover opens for new submissions
  useEffect(() => {
    if (open && mode === 'new' && !editingFeedbackId) {
      setLink(window.location.href);
    }
  }, [open, mode]);

  const submitMutation = useMutation({
    mutationFn: (data) => {
      if (editingFeedbackId) {
        return base44.entities.Feedback.update(editingFeedbackId, data);
      }
      if (data._createIssue) {
        const issueData = { ...data };
        delete issueData._createIssue;
        return base44.entities.Issue.create(issueData);
      }
      return base44.entities.Feedback.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['myFeedback'] });
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['myIssues'] });
      resetForm();
      setMode('new');
      setOpen(false);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    if (isOrgType && !editingFeedbackId) {
      submitMutation.mutate({
        _createIssue: true,
        title: title.trim(),
        description: description.trim(),
        category: feedbackType,
        severity: severity || 'medium',
        reported_by: user?.email,
        reported_at: new Date().toISOString(),
        source: 'manual',
        status: 'open',
        classification_gate1: 'pending',
        classification_gate2: 'pending',
        frequency_signal: 'first_occurrence',
        attachments: attachments.length > 0 ? attachments : undefined
      });
    } else {
      /* ── ProjectRegistry routing lookup ──
         FB-69fcc224 Stage 1: project_id is the canonical FK and required.
         If sourceApp doesn't match a registered ProjectRegistry.label, surface
         the error to the user instead of silently submitting with no routing. */
      const matchedProject = (Array.isArray(projectRegistry) ? projectRegistry : [])
        .find(p => p.label === sourceApp);

      if (!matchedProject?.id) {
        const validLabels = (Array.isArray(projectRegistry) ? projectRegistry : [])
          .map(p => p.label).filter(Boolean).join(', ');
        alert(
          'Cannot submit: source app "' + (sourceApp || '(empty)') + '" is not a registered project.\n\n' +
          'Pick one of: ' + (validLabels || '(no projects registered)')
        );
        return;
      }

      const payload = {
        type: feedbackType,
        title: title.trim(),
        link: link.trim(),
        attachments,
        submitted_by: user?.email,
        intake_mode: 'bug',
        status: editingFeedbackId ? undefined : 'open',
        project_id: matchedProject.id,
      };
      if (editingFeedbackId) delete payload.status;
      if (feedbackType === 'bug') {
        payload.steps_to_reproduce = stepsToReproduce.trim();
        payload.expected_behavior = expectedBehavior.trim();
        payload.actual_behavior = actualBehavior.trim();
      } else {
        payload.description = description.trim();
        payload.intake_mode = 'feature';
      }
      submitMutation.mutate(payload);
    }
  };

  const handleSelectSubmitted = (item) => {
    loadFeedbackIntoForm(item);
    setMode('edit');
  };

  const handleClose = (val) => {
    setOpen(val);
    if (!val) {
      resetForm();
      setMode('new');
    }
  };

  /* ── AI Mode open/close ──
     Open: toggle Dialog visibility. If we already have an aiSessionId from
           a prior open, IntakeChat will use it to resume. Otherwise it
           starts fresh.
     Close: hide the Dialog, but KEEP aiSessionId so the next open resumes.
     We only clear aiSessionId when the session is submitted (IntakeChat
     will call `onSessionCreated(newId)` after "Submit another"). */
  const openAiMode = () => {
    setAiOpen(true);
  };

  /* ── Switch from AI Mode to standard form ── */
  const handleSwitchToForm = () => {
    setAiOpen(false);
    setTimeout(() => {
      resetForm();
      setMode('new');
      setOpen(true);
    }, 150);
  };

  const isFormMode = mode === 'new' || mode === 'edit';

  return (
    <TooltipProvider>
      {/* ═══ AI MODE DIALOG ═══
          Dialog visibility is controlled by `aiOpen`. IntakeChat remounts
          on each open (Radix unmounts on close), but session state persists
          via the server-side IntakeSession entity keyed by `aiSessionId`. */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent
          aria-describedby={undefined}
          className="p-0 bg-slate-900 border-slate-700 sm:max-w-[580px] h-[700px] max-h-[90vh] flex flex-col overflow-hidden"
        >
          <DialogTitle className="sr-only">Talk to REEF</DialogTitle>
          <IntakeChat
            onClose={() => setAiOpen(false)}
            onSwitchToForm={handleSwitchToForm}
            sourceApp={detectedSourceApp}
            resumeSessionId={aiSessionId}
            onSessionCreated={setAiSessionId}
          />
        </DialogContent>
      </Dialog>

      {/* ═══ STANDARD FORM POPOVER ═══ */}
      <Popover open={open} onOpenChange={handleClose}>
        {/* ═══ BUTTON ═══ */}
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              >
                <MessageSquarePlus className="w-5 h-5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Report Bug / Feature / Issue</p>
          </TooltipContent>
        </Tooltip>

        <PopoverContent className="w-96 p-0 bg-slate-900 border-slate-700" align="end">
          {/* Header with toggle */}
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {mode === 'edit' && (
                <button
                  onClick={() => { resetForm(); setMode('list'); }}
                  className="p-1 rounded hover:bg-slate-800 text-slate-400 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <div>
                <h3 className="font-semibold text-slate-100">
                  {mode === 'new' ? 'Report Something' : mode === 'list' ? 'My Submissions' : 'Edit Submission'}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {mode === 'new' ? 'Standard form — or ' : mode === 'list' ? 'View and edit your submissions' : 'Update the details below'}
                  {mode === 'new' && (
                    <button
                      onClick={() => { setOpen(false); setTimeout(() => openAiMode(), 150); }}
                      data-testid="reef-intake-trigger"
                      className="text-amber-400 hover:text-amber-300 underline underline-offset-2"
                    >
                      talk to REEF
                    </button>
                  )}
                </p>
              </div>
            </div>

            {/* Toggle switch */}
            {mode !== 'edit' && (
              <div className="flex bg-slate-800 rounded-md p-0.5 text-[10px] font-medium shrink-0">
                <button
                  onClick={() => { resetForm(); setMode('new'); }}
                  className={`px-2 py-1 rounded transition-colors ${mode === 'new' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  New
                </button>
                <button
                  onClick={() => { resetForm(); setMode('list'); }}
                  className={`px-2 py-1 rounded transition-colors ${mode === 'list' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Submitted
                </button>
              </div>
            )}
          </div>

          {/* Content */}
          {isFormMode ? (
            <FeedbackForm
              feedbackType={feedbackType} setFeedbackType={setFeedbackType}
              title={title} setTitle={setTitle}
              link={link} setLink={setLink}
              description={description} setDescription={setDescription}
              stepsToReproduce={stepsToReproduce} setStepsToReproduce={setStepsToReproduce}
              expectedBehavior={expectedBehavior} setExpectedBehavior={setExpectedBehavior}
              actualBehavior={actualBehavior} setActualBehavior={setActualBehavior}
              severity={severity} setSeverity={setSeverity}
              sourceApp={sourceApp} setSourceApp={setSourceApp}
              projectOptions={projectOptions}
              attachments={attachments} setAttachments={setAttachments}
              uploading={uploading} setUploading={setUploading}
              isEditing={!!editingFeedbackId}
              isPending={submitMutation.isPending}
              onSubmit={handleSubmit}
            />
          ) : (
            <SubmittedFeedbackList
              onSelect={handleSelectSubmitted}
              autoSelectId={autoSelectFeedbackId}
              onAutoSelectHandled={() => setAutoSelectFeedbackId(null)}
            />
          )}
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}
